// Listing run status enum + transition map.
// Data, not function — imported by both server (validator) and client (UI).

export const LISTING_STATUSES = [
  "open",
  "importing",
  "imported_with_errors",
  "imported",
  "failed",
  "cancelled",
] as const

export type ListingStatus = (typeof LISTING_STATUSES)[number]

export const TERMINAL_STATUSES = [
  "imported",
  "failed",
  "cancelled",
] as const satisfies readonly ListingStatus[]

export type TerminalListingStatus = (typeof TERMINAL_STATUSES)[number]

export const VALID_TRANSITIONS: Record<ListingStatus, readonly ListingStatus[]> = {
  // open → imported is the BACKFILL edge: a run whose feed reached the
  // marketplace OUTSIDE nova-ai (e.g. a manual upload in the operator UI) never
  // passed through `importing`, so `nova runs mark-imported` records the
  // terminal state directly. Normal nova-driven uploads still go open →
  // importing → imported.
  open: ["importing", "imported", "failed", "cancelled"],
  // An operator may explicitly abandon a known-bad active import and reopen
  // the same run for repair. Late evidence remains on the historical attempt
  // but cannot alter the run while it is open.
  importing: ["open", "imported_with_errors", "imported", "failed", "cancelled"],
  imported_with_errors: ["importing", "failed", "cancelled"],
  // A successfully imported run may also be explicitly reopened for repair.
  // The dedicated reopen service clears approval in the SAME write; heal never
  // takes either reopen edge implicitly.
  imported: ["open"],
  failed: [],
  cancelled: [],
}

export const TRACKED_SKU_SOURCES = ["nova", "manual", "backimport"] as const
export type TrackedSkuSource = (typeof TRACKED_SKU_SOURCES)[number]

// How a listing run came to exist. Orthogonal to status: `operator` runs are
// the human-driven listing work the dashboard shows by default; `qa` runs are
// created by the scheduled QA loop, scoped out of the default list, and reaped
// after each tick. Marketplace-neutral — never a vendor name.
export const RUN_ORIGINS = ["operator", "automatic", "qa"] as const
export type RunOrigin = (typeof RUN_ORIGINS)[number]

// Verdict a delta-tick change detection reaches for one parent SKU (issue
// #131): `new` — the parent just appeared in the Magento catalog; `relist` —
// an important attribute changed (worth acting on); `ignore` — only trivial
// attributes changed. Vocabulary owned here (the report crosses app
// boundaries: API builds it, CLI renders it); the importance RULES live in
// `apps/api/src/modules/products/change-significance.ts`.
export const PRODUCT_CHANGE_VERDICTS = ["new", "relist", "ignore"] as const
export type ProductChangeVerdict = (typeof PRODUCT_CHANGE_VERDICTS)[number]

export const ROLES = ["admin", "operator"] as const
export type Role = (typeof ROLES)[number]

// Strict-greater rank map: requireRole("operator") admits operator and admin.
// Adding a third role only requires inserting a number here.
export const ROLE_RANK: Record<Role, number> = {
  operator: 1,
  admin: 2,
}

export function hasMinRole(actual: Role, min: Role): boolean {
  return ROLE_RANK[actual] >= ROLE_RANK[min]
}
